import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Landmark, Loader2, Upload, AlertTriangle, CheckCircle2, UserCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import api from '../../lib/api';
import { formatImporte, formatDateDMY } from '../../lib/utils';

const Senales = ({ ev }) => {
  if (!ev) return <span className="text-xs text-slate-400">Sin sugerencia</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {ev.nombre_ok && <span className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700"><UserCheck className="w-3 h-3" />{ev.cuit_ok ? 'CUIT' : 'Nombre'}</span>}
      <span className={`rounded px-1.5 py-0.5 text-[11px] ${ev.monto_ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
        {ev.diff_pct > 0 ? '+' : ''}{ev.diff_pct}%
      </span>
      {ev.alerta && <span className="inline-flex items-center gap-0.5 rounded bg-rose-50 px-1.5 py-0.5 text-[11px] text-rose-700"><AlertTriangle className="w-3 h-3" />Revisar diferencia</span>}
    </div>
  );
};

const evaluar = (mov, f) => {
  if (!f) return null;
  const diff = f.esperado ? (mov.monto - f.esperado) / f.esperado : 1;
  const prev = mov.sugerencia?.unidad_id === f.unidad_id ? mov.sugerencia : null;
  return { unidad_id: f.unidad_id, diff_pct: +(diff * 100).toFixed(2), monto_ok: Math.abs(diff) <= 0.020001,
    nombre_ok: prev?.nombre_ok || false, cuit_ok: prev?.cuit_ok || false, alerta: (prev?.nombre_ok) && Math.abs(diff) > 0.020001 };
};

const FilaMov = ({ mov, facturas, sel, onSel }) => {
  const f = facturas.find(x => x.unidad_id === sel.unidad_id);
  const ev = evaluar(mov, f);
  const menor = f && mov.monto < f.esperado - 1;
  return (
    <tr className={`border-t border-slate-100 ${sel.aprobado ? 'bg-emerald-50/40' : ''}`} data-testid={`extracto-mov-${mov.id}`}>
      <td className="px-2 py-2 text-xs whitespace-nowrap">{formatDateDMY(mov.fecha)}</td>
      <td className="px-2 py-2 text-xs max-w-[260px]"><div className="truncate" title={mov.descripcion}>{mov.descripcion}</div>{mov.nombre && <div className="text-slate-400 truncate">{mov.nombre}</div>}</td>
      <td className="px-2 py-2 text-right font-mono text-sm whitespace-nowrap">$ {formatImporte(mov.monto, 2)}</td>
      <td className="px-2 py-2">
        <select className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-sm" value={sel.unidad_id || ''}
          onChange={(e) => onSel({ ...sel, unidad_id: e.target.value || null, aprobado: false })} data-testid={`extracto-factura-${mov.id}`}>
          <option value="">— Ninguna —</option>
          {facturas.map(x => <option key={x.unidad_id} value={x.unidad_id}>{x.unidad_nombre} · {x.inquilino} · $ {formatImporte(x.esperado, 0)}</option>)}
        </select>
        {f && <div className="mt-0.5 text-[11px] text-slate-500">Factura $ {formatImporte(f.total, 2)}{f.retencion ? ` − retención ${f.retencion_pct}% $ ${formatImporte(f.retencion, 2)}` : ''}{f.cobrado_parcial ? ` − cobrado $ ${formatImporte(f.cobrado_parcial, 2)}` : ''}</div>}
      </td>
      <td className="px-2 py-2"><Senales ev={ev} /></td>
      <td className="px-2 py-2">
        {f && menor ? (
          <select className="rounded border border-slate-200 bg-white px-1 py-1 text-xs" value={sel.modo} onChange={(e) => onSel({ ...sel, modo: e.target.value })} data-testid={`extracto-modo-${mov.id}`}>
            <option value="parcial">Parcial</option><option value="total">Total</option>
          </select>
        ) : f ? <span className="text-xs text-slate-500">Total</span> : null}
      </td>
      <td className="px-2 py-2 text-center">
        <input type="checkbox" className="w-4 h-4 accent-emerald-600" disabled={!f} checked={!!sel.aprobado}
          onChange={(e) => onSel({ ...sel, aprobado: e.target.checked })} data-testid={`extracto-aprobar-${mov.id}`} />
      </td>
    </tr>
  );
};

export const ExtractoBancarioButton = ({ edificioId, periodo, onDone }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [sels, setSels] = useState({});
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const subir = async (file) => {
    if (!file) return;
    setLoading(true); setData(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post(`/cobros/${edificioId}/extracto`, fd, { params: { periodo }, timeout: 180000, headers: { 'Content-Type': 'multipart/form-data' } });
      setData(r.data);
      const init = {};
      r.data.movimientos.forEach(m => {
        const f = r.data.facturas.find(x => x.unidad_id === m.sugerencia?.unidad_id);
        init[m.id] = { unidad_id: m.sugerencia?.unidad_id || null, aprobado: false, modo: f && m.monto < f.esperado * 0.979999 ? 'parcial' : 'total' };
      });
      setSels(init);
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'No se pudo leer el extracto');
    } finally { setLoading(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const aprobados = data ? data.movimientos.filter(m => sels[m.id]?.aprobado && sels[m.id]?.unidad_id) : [];
  const aplicar = async () => {
    const ids = aprobados.map(m => sels[m.id].unidad_id);
    if (new Set(ids).size !== ids.length && !window.confirm('Hay una factura asignada a más de un movimiento. ¿Continuar?')) return;
    setSaving(true);
    try {
      const r = await api.post(`/cobros/${edificioId}/extracto/aplicar`, {
        asignaciones: aprobados.map(m => ({ unidad_id: sels[m.id].unidad_id, fecha: m.fecha, monto: m.monto, modo: sels[m.id].modo })),
      }, { params: { periodo } });
      toast.success(`${r.data.aplicadas.length} cobros registrados`);
      if (r.data.errores.length) toast.warning(r.data.errores.join(' · '));
      onDone?.(); setOpen(false); setData(null);
    } catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'No se pudieron registrar los cobros'); } finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-2 shrink-0" data-testid="extracto-bancario-btn">
        <Landmark className="w-4 h-4" /> Cargar extracto bancario
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] xl:max-w-7xl" data-testid="extracto-dialog">
          <DialogHeader>
            <DialogTitle>Conciliar extracto bancario</DialogTitle>
            <DialogDescription>Subí el extracto (PDF, Excel o CSV). Cada ingreso trae una factura sugerida por monto (factura − retenciones, ±2%) y por nombre/CUIT. Revisá y tildá las correctas.</DialogDescription>
          </DialogHeader>
          <input ref={inputRef} type="file" accept=".pdf,.xlsx,.xls,.csv,.txt" className="hidden" onChange={(e) => subir(e.target.files?.[0])} data-testid="extracto-file-input" />
          {!data && (
            <button type="button" onClick={() => inputRef.current?.click()} disabled={loading}
              className="w-full rounded-lg border-2 border-dashed border-slate-300 py-10 text-slate-500 hover:border-slate-400" data-testid="extracto-subir">
              {loading ? <span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Leyendo movimientos…</span>
                : <span className="inline-flex items-center gap-2"><Upload className="w-5 h-5" />Elegir archivo del extracto</span>}
            </button>
          )}
          {data && (
            <>
              <p className="text-xs text-slate-500">{data.movimientos.length} ingresos leídos · {data.movimientos.filter(m => m.sugerencia).length} con sugerencia · {data.facturas.length} facturas pendientes</p>
              <div className="overflow-auto max-h-[55vh] rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10 text-xs uppercase tracking-wider text-slate-500">
                    <tr><th className="px-2 py-2 text-left">Fecha</th><th className="px-2 py-2 text-left">Movimiento</th><th className="px-2 py-2 text-right">Monto</th>
                      <th className="px-2 py-2 text-left w-[30%]">Factura sugerida</th><th className="px-2 py-2 text-left">Coincidencia</th><th className="px-2 py-2 text-left">Cobro</th><th className="px-2 py-2 text-center">Aprobar</th></tr>
                  </thead>
                  <tbody>
                    {data.movimientos.map(m => <FilaMov key={m.id} mov={m} facturas={data.facturas} sel={sels[m.id] || {}} onSel={(v) => setSels(s => ({ ...s, [m.id]: v }))} />)}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => setData(null)} data-testid="extracto-otro">Subir otro extracto</Button>
                <Button onClick={aplicar} disabled={!aprobados.length || saving} data-testid="extracto-aplicar">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Registrar {aprobados.length} cobros
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
